/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageSmile } from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { StyledLoading } from 'src/common/components';
import { useTrackEventMutation } from 'src/common/slices';
import { useGetBadgesQuery } from 'src/features/badges/badges.slice';
import { FeatureFlagName } from 'src/common/constants';
import { useFeatureFlag } from 'src/common/hooks';
import { PathProgress } from '../completion-modal/components/steps/path-progress';
import { StyledButtonWrapper, StyledCongratulationsFullScreen, StyledContainer, StyledContentWrapper, StyledFooter } from './congratulations-full-screen.styles';
import { RecordsStep } from './components/records-step';
import { CongratulationsFullScreenProps } from './congratulations-full-screen.types';
import { SharingBadgeStep } from './components/sharing-badge-step';
export const CongratulationFullScreen = ({
  currentRoom,
  setIsCongratulationsFullScreenOpen,
  isLoading,
  onClose
}: CongratulationsFullScreenProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLeaveFeedbackOpen, setIsLeaveFeedbackOpen] = useState(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);
  const [trackEvent] = useTrackEventMutation();
  const {
    data: badgesData,
    isLoading: isBadgesLoading
  } = useGetBadgesQuery();
  const {
    enabled: isShareBadgeEnabled
  } = useFeatureFlag(FeatureFlagName.SHARE_BADGE_CONGRATULATIONS_PAGE);
  const awardedBadge = badgesData?.data?.badges?.find(badge => badge.toEarn?.includes(currentRoom?.code ?? ''));
  const navigate = useNavigate();
  const {
    nextRoom
  } = currentRoom || {};
  const roomCode = currentRoom?.code ?? '';
  const numberOfSteps = useMemo(() => {
    let steps = 2;
    if (awardedBadge && isShareBadgeEnabled) {
      steps += 1;
    }
    if (nextRoom) {
      steps += 1;
    }
    return steps;
  }, [awardedBadge, isShareBadgeEnabled, nextRoom]);
  const SharingStep = React.lazy(() => import('./components/sharing-step').then(module => ({
    default: module.SharingStep
  })));
  const FeedbackModal = React.lazy(() => import('./components/feedback-modal').then(module => ({
    default: module.FeedbackModal
  })));
  const handleContinue = useCallback(() => {
    if (currentStep < numberOfSteps) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, numberOfSteps]);
  const handleGoBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);
  const handleClose = useCallback(() => {
    setIsCongratulationsFullScreenOpen(false);
    onClose?.();
  }, [onClose, setIsCongratulationsFullScreenOpen]);
  const handleGoToNextRoom = async () => {
    // eslint-disable-next-line no-void
    await trackEvent({
      event: 'click-next-room-button',
      properties: {
        current_room: currentRoom?.code,
        next_room: nextRoom?.code,
        clicked_on: 'from_popup'
      }
    });
    navigate(`/jr/${nextRoom?.code ?? ''}`);
  };
  const renderPathProgressStep = () => <StyledContentWrapper data-content="next-room" data-sentry-element="StyledContentWrapper" data-sentry-component="renderPathProgressStep" data-sentry-source-file="congratulations-full-screen.tsx">
      <PathProgress currentRoom={currentRoom} data-sentry-element="PathProgress" data-sentry-source-file="congratulations-full-screen.tsx" />
    </StyledContentWrapper>;
  const renderContent = () => {
    switch (currentStep) {
      case 1:
        {
          return <RecordsStep currentRoom={currentRoom} isLoading={isLoading} isFeedbackSent={isFeedbackSent} />;
        }
      case 2:
        {
          return <Suspense fallback={<StyledLoading />}>
            <SharingStep currentRoom={currentRoom} isLoading={isLoading} />
          </Suspense>;
        }
      case 3:
        {
          if (awardedBadge && isShareBadgeEnabled) {
            return <SharingBadgeStep awardedBadge={awardedBadge} roomCode={roomCode} isLoading={isBadgesLoading} />;
          }
          if (nextRoom) {
            return renderPathProgressStep();
          }
          return null;
        }
      case 4:
        {
          if (awardedBadge && isShareBadgeEnabled && nextRoom) {
            return renderPathProgressStep();
          }
          return null;
        }
      default:
        {
          return null;
        }
    }
  };
  const renderFooterButtons = () => {
    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === numberOfSteps;
    const isSecondLastStep = currentStep === numberOfSteps - 1;
    const leftButton = isFirstStep ? <Button variant="outlined" color="secondary" onClick={() => setIsLeaveFeedbackOpen(true)}>
        <FontAwesomeIcon icon={faMessageSmile} /> Leave Feedback
      </Button> : <Button variant="outlined" color="secondary" onClick={handleGoBack}>
        <FontAwesomeIcon icon={faArrowLeft} /> Go back
      </Button>;
    let rightButtons;
    if (isLastStep && nextRoom) {
      rightButtons = <StyledButtonWrapper>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button color="primary" onClick={handleGoToNextRoom}>
            Go to next room
          </Button>
        </StyledButtonWrapper>;
    } else if (currentStep < numberOfSteps) {
      rightButtons = <Button variant="outlined" color="primary" onClick={handleContinue}>
          Next
        </Button>;
    } else if (isSecondLastStep && !nextRoom || isLastStep && !nextRoom) {
      rightButtons = <StyledButtonWrapper>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            Close
          </Button>
        </StyledButtonWrapper>;
    }
    return <StyledFooter data-sentry-element="StyledFooter" data-sentry-component="renderFooterButtons" data-sentry-source-file="congratulations-full-screen.tsx">
        {leftButton}
        {rightButtons}
      </StyledFooter>;
  };
  return <StyledCongratulationsFullScreen role="dialog" data-sentry-element="StyledCongratulationsFullScreen" data-sentry-component="CongratulationFullScreen" data-sentry-source-file="congratulations-full-screen.tsx">
      <StyledContainer data-sentry-element="StyledContainer" data-sentry-source-file="congratulations-full-screen.tsx">
        {renderContent()}
        {renderFooterButtons()}
      </StyledContainer>
      <Suspense fallback={<div>...Loading</div>} data-sentry-element="Suspense" data-sentry-source-file="congratulations-full-screen.tsx">
        <FeedbackModal isOpen={isLeaveFeedbackOpen} onClose={() => {
        setIsLeaveFeedbackOpen(false);
        setIsFeedbackSent(false);
      }} roomCode={roomCode} setIsFeedbackSent={setIsFeedbackSent} data-sentry-element="FeedbackModal" data-sentry-source-file="congratulations-full-screen.tsx" />
      </Suspense>
    </StyledCongratulationsFullScreen>;
};